import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Select, SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { ProfilesLocation } from 'src/app/api/types/partnerships';

@Component({
  selector: 'app-profiles-location-dropdown',
  templateUrl: './profiles-location-dropdown.component.html',
  styleUrls: ['./profiles-location-dropdown.component.scss'],
  imports: [SelectModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProfilesLocationDropdownComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilesLocationDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  private baseLocations: ProfilesLocation[] = [];

  @Input() set options(value: ProfilesLocation[]) {
    this.baseLocations = [...value]
      .filter((loc) => loc.id !== '')
      .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
    this.rebuildDisplayedOptions();
  }

  get options(): ProfilesLocation[] {
    return this.displayedOptions;
  }

  displayedOptions: ProfilesLocation[] = [];

  @Input() allowEmpty = false;


  @Input() disableEdit = false;

  @Input() autoDisplayFirst = true;

  @Input() formValueType: 'ProfileLocation' | 'idString' = 'idString';

  @Input() virtualScroll = false;

  @Input() virtualScrollItemSize: number | null = 38;

  private _placeholder = '';
  @Input() set placeholder(value: string) {
    this._placeholder = value;
  }

  get placeholder(): string {
    return this._placeholder;
  }

  @Output() readonly selectionChange = new EventEmitter<ProfilesLocation | null>();

  readonly emptyLocation: ProfilesLocation = {
    name: 'None',
    id: '',
    region: '',
    active: 1,
    public: 1,
    domestic: 1,
    domesticRegion: '',
    readinessLocation: 0,
    airport1: '',
    airport2: '',
    airport3: '',
    reentryStatus: 0,
    displayAdvisory: 0,
    landing365: 0,
    opsRegionId: 0,
  };

  form = new FormGroup({
    location: new FormControl<ProfilesLocation | null>(null),
  });

  value: ProfilesLocation | null = null;

  private pendingLocId: string | null = null;

  onChange: (value: ProfilesLocation | string | null) => void = () => {};
  onTouched: () => void = () => {};
  disabled = false;
  touched = false;

  subscriptions: Subscription[] = [];

  @ViewChild(Select) dropdown?: Select;

  constructor(private readonly chRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((changes) => {
        if (changes.location !== undefined) {
          this.value = changes.location ?? null;
          this.pendingLocId = this.value?.id ?? null;
          this.selectionChange.emit(this.value);
          switch (this.formValueType) {
            case 'ProfileLocation':
              this.onChange(this.value);
              break;
            case 'idString':
              this.onChange(this.value?.id ?? '');
              break;
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allowEmpty']) {
      this.rebuildDisplayedOptions();
    }
    if (changes['disableEdit']) {
      this.chRef.markForCheck();
    }
  }

  get readOnlyLabel(): string {
    if (this.value !== null && this.value !== undefined) {
      return this.value.name ?? '';
    }
    if (this.pendingLocId !== null && this.pendingLocId !== '') {
      const loc =
        this.displayedOptions.find((l) => l.id === this.pendingLocId) ??
        this.baseLocations.find((l) => l.id === this.pendingLocId);
      return loc?.name ?? '';
    }
    if (this.pendingLocId === '' && this.allowEmpty) {
      return this.emptyLocation.name ?? '';
    }
    return '';
  }

  writeValue(obj: string | ProfilesLocation | null | undefined): void {
    if (obj === null || obj === undefined || obj === '') {
      this.pendingLocId = null;
      this.value = null;
      this.form.controls.location.setValue(null, { emitEvent: false });
      this.chRef.markForCheck();
      return;
    }
    if (typeof obj === 'string') {
      this.pendingLocId = obj;
      this.applyPendingLocId();
      return;
    }
    this.pendingLocId = obj.id;
    this.value =
      this.displayedOptions.find((loc) => loc.id === obj.id) ?? obj;
    this.form.controls.location.setValue(this.value, { emitEvent: false });
    this.chRef.markForCheck();
  }

  registerOnChange(fn: (value: ProfilesLocation | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
    this.chRef.markForCheck();
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  show(): void {
    this.dropdown?.show();
  }

  hide(): void {
    this.dropdown?.hide();
  }

  focus(): void {
    this.dropdown?.focus();
  }

  resetFilter(): void {
    this.dropdown?.resetFilter();
  }

  private rebuildDisplayedOptions(): void {
    if (this.allowEmpty) {
      this.displayedOptions = [this.emptyLocation, ...this.baseLocations];
    } else {
      this.displayedOptions = [...this.baseLocations];
      if (this.value?.id === '') {
        this.value = null;
        this.form.controls.location.setValue(null, { emitEvent: false });
      }
    }
    this.applyPendingLocId();
    this.chRef.markForCheck();
  }

  private applyPendingLocId(): void {
    if (this.pendingLocId === null) {
      this.value = null;
      this.form.controls.location.setValue(null, { emitEvent: false });
      return;
    }
    if (this.pendingLocId === '') {
      if (this.allowEmpty) {
        this.value = this.emptyLocation;
        this.form.controls.location.setValue(this.value, { emitEvent: false });
      } else {
        this.value = null;
        this.form.controls.location.setValue(null, { emitEvent: false });
      }
      return;
    }
    const resolved =
      this.displayedOptions.find((loc) => loc.id === this.pendingLocId) ??
      this.baseLocations.find((loc) => loc.id === this.pendingLocId) ??
      null;
    this.value = resolved;
    this.form.controls.location.setValue(this.value, { emitEvent: false });
  }
}
